import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Timer } from "lucide-react";
import type { IdleHandle } from "../app/idleLock.js";
import { formatCountdown } from "../lib/idle.js";

const WARN_BELOW_MS = 60_000;

/** Live idle-lock countdown. Turns into a warning in the last minute; any input resets it. */
export function LockCountdown(props: { readonly idle: IdleHandle }): ReactNode {
  const [remaining, setRemaining] = useState<number | undefined>((): number | undefined => props.idle.remainingMs());

  useEffect((): (() => void) => {
    const id = window.setInterval((): void => setRemaining(props.idle.remainingMs()), 1000);
    return (): void => window.clearInterval(id);
  }, [props.idle]);

  if (remaining === undefined) {
    return null;
  }
  const warn = remaining <= WARN_BELOW_MS;
  return (
    <>
      <span
        className={`hidden items-center gap-1.5 rounded-md border px-2 py-1 text-xs tabular-nums transition-colors md:inline-flex ${
          warn ? "border-buoy/40 bg-buoy/10 text-buoy" : "border-transparent text-mist-2"
        }`}
        title="Automatische Sperre bei Inaktivität"
      >
        <Timer size={13} aria-hidden="true" />
        Sperre in {formatCountdown(remaining)}
      </span>
      {warn ? (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-4 z-[60] mx-auto flex w-fit max-w-[92vw] items-center gap-3 rounded-xl border border-buoy/40 bg-ink-2 px-4 py-3 text-sm text-foam shadow-pop animate-pop"
        >
          <Timer size={16} aria-hidden="true" className="shrink-0 text-buoy" />
          <span className="sr-only">Der Tresor sperrt sich in weniger als einer Minute. Drück eine Taste, um weiterzuarbeiten.</span>
          <span aria-hidden="true">
            Der Tresor sperrt sich in <strong className="tabular-nums">{formatCountdown(remaining)}</strong>. Beweg die Maus oder drück eine Taste, um weiterzuarbeiten.
          </span>
        </div>
      ) : null}
    </>
  );
}
